/**
 * SearchTab Component
 *
 * Search and filter layers by name, tags, category, or group.
 */

import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faSearch } from '@fortawesome/free-solid-svg-icons';
import './SearchTab.scss';
import { useLayerDrawer } from '../LayerDrawerContext';
import { LayerCard } from './LayerCard';
import { filterLayersBySearch, debounce } from '../utils/layerDrawerUtils';

export const SearchTab: React.FC = () => {
  const { layers, loading, error, searchTerm, setSearchTerm } = useLayerDrawer();
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Filter layers based on search term
   */
  const filteredLayers = useMemo(() => {
    if (!searchTerm.trim()) {
      return [];
    }
    return filterLayersBySearch(layers, searchTerm);
  }, [layers, searchTerm]);

  /**
   * Debounced search handler
   */
  const debouncedSetSearchTerm = useMemo(
    () => debounce((term: string) => setSearchTerm(term), 300),
    [setSearchTerm]
  );

  /**
   * Handle search input change
   */
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSetSearchTerm(value);
  }, [debouncedSetSearchTerm]);

  /**
   * Clear search
   */
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  }, [setSearchTerm]);

  /**
   * Focus input on tab mount
   */
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  if (loading) {
    return (
      <div className="search-tab">
        <div className="search-tab-loading">
          <div className="loading-spinner"><FontAwesomeIcon icon={faSyncAlt} spin /></div>
          <div className="loading-text">Loading layers...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="search-tab">
        <div className="search-tab-error">
          <div className="error-icon">⚠</div>
          <div className="error-text">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="search-tab">
      <div className="search-tab-header">
        <div className="search-input-container">
          <span className="search-icon"><FontAwesomeIcon icon={faSearch} /></span>
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Search layers by name, tags, category..."
            defaultValue={searchTerm}
            onChange={handleSearchChange}
            aria-label="Search layers"
          />
          {searchTerm && (
            <button
              className="search-clear-btn"
              onClick={handleClearSearch}
              aria-label="Clear search"
              title="Clear"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="search-tab-content">
        {!searchTerm.trim() ? (
          <div className="search-tab-prompt">
            <div className="prompt-icon">🔎</div>
            <div className="prompt-text">Enter a search term</div>
            <div className="prompt-hint">
              Search by layer name, tags, category, or group
            </div>
          </div>
        ) : filteredLayers.length === 0 ? (
          <div className="search-tab-no-results">
            <div className="no-results-icon">🤷</div>
            <div className="no-results-text">No layers found</div>
            <div className="no-results-hint">
              Try a different search term
            </div>
          </div>
        ) : (
          <>
            <div className="search-tab-results-header">
              <div className="results-count">
                {filteredLayers.length} result{filteredLayers.length !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="search-tab-results">
              {filteredLayers.map(layer => (
                <LayerCard
                  key={layer.id}
                  layer={layer}
                  showOpacity={false}
                  showInfo={true}
                  showDragHandle={false}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
